import { useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from "react"
import { useReducedMotion } from "framer-motion"
import { formatFr } from "@/lib/utils"
import "./NumberWheel.css"

/**
 * Molette de saisie numérique (née dans le diagnostic, 2e page) : on fait défiler
 * (arrêt sur chaque valeur), ou − / +. Remplace les champs `type="number"`, qui ne
 * se vidaient pas sur iPhone et affichaient des flèches natives sur ordinateur.
 *
 * La valeur de départ est toujours proposée telle quelle, même hors de la grille
 * (72,4 avec un pas de 0,5) ou hors bornes : enregistrer sans toucher la molette
 * ne doit jamais modifier une valeur existante. Elle reste dans la liste, entre ses
 * deux voisines ; − et + ramènent ensuite sur la grille.
 */

const ROW = 44

/** Nombre de décimales d'un pas (1 → 0, 0,5 → 1, 0,1 → 1). */
function decimalsOf(n: number): number {
  for (let d = 0; d < 3; d++) if (Math.abs(Number(n.toFixed(d)) - n) < 1e-9) return d
  return 2
}

function buildValues(min: number, max: number, step: number, start: number): number[] {
  const d = decimalsOf(step)
  const count = Math.round((max - min) / step)
  const values: number[] = []
  for (let i = 0; i <= count; i++) values.push(Number((min + i * step).toFixed(d)))
  if (Number.isFinite(start) && !values.some((v) => Math.abs(v - start) < 1e-9)) {
    const at = values.findIndex((v) => v > start)
    values.splice(at === -1 ? values.length : at, 0, start)
  }
  return values
}

export function NumberWheel({
  id, label, unit, min, max, step = 1, value, onChange, labelHidden,
}: {
  id: string
  label: string
  unit: string
  min: number
  max: number
  /** Pas de la grille (1 par défaut). */
  step?: number
  value: number
  onChange: (v: number) => void
  /** Libellé lu par les lecteurs d'écran seulement (le titre autour en tient lieu). */
  labelHidden?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const reduceMotion = useReducedMotion()
  // Position de départ seulement : ensuite c'est le défilement qui fait la valeur.
  const [start] = useState(value)
  const values = useMemo(() => buildValues(min, max, step, start), [min, max, step, start])
  const stepDecimals = decimalsOf(step)
  const format = (n: number) => formatFr(n, Math.max(stepDecimals, decimalsOf(n)))

  const found = values.findIndex((v) => Math.abs(v - value) < 1e-9)
  const index = found === -1 ? 0 : found
  const last = values.length - 1

  useLayoutEffect(() => {
    if (ref.current) ref.current.scrollTop = index * ROW
  }, [])

  const handleScroll = () => {
    const el = ref.current
    if (!el) return
    const v = values[Math.min(last, Math.max(0, Math.round(el.scrollTop / ROW)))]
    if (v !== value) onChange(v)
  }

  const stepTo = (i: number) => {
    const next = Math.min(last, Math.max(0, i))
    onChange(values[next])
    ref.current?.scrollTo({ top: next * ROW, behavior: reduceMotion ? "auto" : "smooth" })
  }

  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowUp") { e.preventDefault(); stepTo(index - 1) }
    else if (e.key === "ArrowDown") { e.preventDefault(); stepTo(index + 1) }
  }

  const stepWord = step === 1 ? "un" : format(step)

  return (
    <div className={labelHidden ? "nw nw-bare" : "nw"}>
      <span className={labelHidden ? "sr-only" : "nw-label"} id={`${id}-label`}>{label}</span>
      <div className="nw-controls">
        <button type="button" className="nw-step" onClick={() => stepTo(index - 1)} disabled={index <= 0} aria-label={`${label} : moins ${stepWord}`}>−</button>
        <div className="nw-frame">
          <div className="nw-band" aria-hidden="true" />
          <div
            ref={ref}
            className="nw-wheel"
            onScroll={handleScroll}
            onKeyDown={handleKey}
            tabIndex={0}
            role="spinbutton"
            aria-labelledby={`${id}-label`}
            aria-valuemin={values[0]}
            aria-valuemax={values[last]}
            aria-valuenow={value}
            aria-valuetext={`${format(value)} ${unit}`}
          >
            {values.map((n) => (
              <div key={n} className="nw-item" data-current={n === value} aria-hidden="true">
                {format(n)}<span className="nw-unit">{unit}</span>
              </div>
            ))}
          </div>
        </div>
        <button type="button" className="nw-step" onClick={() => stepTo(index + 1)} disabled={index >= last} aria-label={`${label} : plus ${stepWord}`}>+</button>
      </div>
    </div>
  )
}
